import { type NodeProps, Position } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';

import {
  isDeclineRecurringAction,
  type NodeItem,
  type ParentNode,
  type RecurringActionNodeData,
  type RetryInterval,
} from '@models/flows';
import { Connector, renderRetryInterval } from '@utils/flows';

import { useNodeError } from '@hooks/useNodeError';

import {
  ConnectionPoint,
  DeleteButton,
  ErrorToolTip,
} from '@components/Flows/components/partials/index';

import { initialRecurringActionNode } from '@features/chargerFlows/validation/validation-helpers';

import s from './RecurringActionNode.module.css';

type RecurringActionProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

function RecurringActionNode({ id, type, data, onEdit, onDelete }: RecurringActionProps) {
  const recurringActionData = data as unknown as RecurringActionNodeData;
  const nodeItems = recurringActionData?.items || [initialRecurringActionNode];
  const parentNode: ParentNode = {
    id,
    type,
    data: recurringActionData,
  };
  const recurringNodeItem = nodeItems[0];
  const errors = useNodeError(id);

  const handleEdit = () => {
    onEdit(parentNode, recurringNodeItem);
  };

  return (
    <div className={`${s.recurringActionContainer} ${errors ? s.errorBorder : ''}`}>
      <h1 className={s.title}>Recurring Action Node</h1>
      <ErrorToolTip isErrorVisible={Boolean(errors)} messages={errors?.messages} />
      <DeleteButton onDelete={() => onDelete(id)} />
      <div className={s.recurringActionBlock}>
        <div className={s.recurringActionWrapper}>
          <p className={s.recurringActionLabel}>
            <span className={s.boldedText}>Action: </span>
            <span className={s.valueText}>{recurringNodeItem.action}</span>
          </p>
          {!isDeclineRecurringAction(recurringNodeItem.action || '') && (
            <>
              <p className={s.recurringActionLabel}>
                <span className={s.boldedText}>Retry every: </span>
                <span className={s.valueText}>
                  {renderRetryInterval(recurringNodeItem.retryInterval as RetryInterval)}
                </span>
              </p>
              <p className={s.recurringActionLabel}>
                <span className={s.boldedText}>Max retries: </span>
                <span className={s.valueText}>{recurringNodeItem.maxRetries}</span>
              </p>
            </>
          )}
        </div>
        <button type="button" className={s.editButton} onClick={handleEdit}>
          <EditIcon />
        </button>
      </div>
      <ConnectionPoint type={Connector.Target} position={Position.Left} id="incoming" />
      <ConnectionPoint type={Connector.Source} position={Position.Right} id="outgoing" />
    </div>
  );
}

export default RecurringActionNode;
